import { lazy, Suspense, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { initSentry, SentryErrorBoundary } from './lib/sentry'
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary'
import PageLoading from './components/PageLoading'
import { sweepExpiredDrafts } from './lib/draftStorage'
import { AuthProvider, useAuth } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import { PortalAuthProvider } from './context/PortalAuthContext'
import { ToastProvider } from './context/ToastContext'
import { PromptProvider } from './context/PromptProvider'
import { useFeatureFlags } from './hooks/useFeatureFlags'
import useDisplayZoomRouteSync from './hooks/useDisplayZoomRouteSync'
import PortalLayout from './components/PortalLayout'
import Layout from './components/Layout'
import Login from './pages/Login'
import PublicIntake from './pages/PublicIntake'
import EngagementSign from './pages/EngagementSign'
import QuestionnaireFill from './pages/QuestionnaireFill'
import RecordUpload from './pages/RecordUpload'
import ConsultCancel from './pages/ConsultCancel'
import PublicPay from './pages/PublicPay'
// Desktop 1.0.13 timer bar (James, 2026-10-01) — EAGER, not lazy: it renders
// in a ~48px-tall window where the lazy-route fallback (PageLoading) would
// flash a full-page spinner on every load.  It is a single small component.
import TimerBar from './pages/TimerBar'
// Dashboard stays EAGER — it's the landing page after login.  T2-B
// pre-code review confirmed by all 5 reviewers: `webpackPrefetch` on
// the landing route does nothing (prefetch fires AFTER main.js finishes
// executing, i.e. AFTER Dashboard's own React.lazy trigger has already
// requested the chunk).  Keeping it eager avoids the redundant-fetch
// race and the ~300ms first-paint delay users would see after login.
import Dashboard from './pages/Dashboard'
import Clients from './pages/Clients'
import Matters from './pages/Matters'
import MatterDetail from './pages/MatterDetail'
import Tasks from './pages/Tasks'
import ClientDetail from './pages/ClientDetail'
import Billing from './pages/Billing'
import Dailies from './pages/Dailies'
import Communications from './pages/Communications'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import EmergencyLock from './pages/EmergencyLock'
import Contacts from './pages/Contacts'
import SOLTracker from './pages/SOLTracker'
import Profile from './pages/Profile'

// Heavy pages lazy-loaded to trim the main bundle.  T2-B added
// Calendar, TimeEntries, InvoiceDetail, Receipts to this list —
// each is ~700-1500 LOC + heavy deps (jsPDF, date-fns).  Layout
// idle-prefetches these after mount so offline nav still works.
const Calendar = lazy(() => import('./pages/Calendar'))
const TimeEntries = lazy(() => import('./pages/TimeEntries'))
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'))
const Receipts = lazy(() => import('./pages/Receipts'))
const Reports = lazy(() => import('./pages/Reports'))
const AttorneyProductivity = lazy(() => import('./pages/AttorneyProductivity'))
const CustomReports = lazy(() => import('./pages/CustomReports'))
const Trust = lazy(() => import('./pages/Trust'))
const Import = lazy(() => import('./pages/Import'))
const CourtRules = lazy(() => import('./pages/CourtRules'))
const BatchInvoicing = lazy(() => import('./pages/BatchInvoicing'))
const ConflictChecker = lazy(() => import('./pages/ConflictChecker'))
const SearchResults = lazy(() => import('./pages/SearchResults'))
const AuditLog = lazy(() => import('./pages/AuditLog'))
const ActivityFeed = lazy(() => import('./pages/ActivityFeed'))
const ClientMerge = lazy(() => import('./pages/ClientMerge'))
const ContactDuplicates = lazy(() => import('./pages/ContactDuplicates'))
const MatterDuplicates = lazy(() => import('./pages/MatterDuplicates'))
const Feedback = lazy(() => import('./pages/Feedback'))
const TimeEntryDuplicates = lazy(() => import('./pages/TimeEntryDuplicates'))
const Deadlines = lazy(() => import('./pages/Deadlines'))
const Discovery = lazy(() => import('./pages/Discovery'))
const PopupLayout = lazy(() => import('./components/PopupLayout'))
const PopupMatter = lazy(() => import('./pages/PopupMatter'))
const PopupTimer = lazy(() => import('./pages/PopupTimer'))
const Help = lazy(() => import('./pages/Help'))
const AdminHome = lazy(() => import('./pages/AdminHome'))
const CustomFields = lazy(() => import('./pages/CustomFields'))
const Roles = lazy(() => import('./pages/Roles'))
// Ship 7a — Chart of Accounts admin page.  Lazy so the CoA tree code +
// modals don't hit the main bundle for users who never touch it.
const Accounting = lazy(() => import('./pages/Accounting'))


// Admin / intake / config pages — lazy so they stay out of the main bundle.
// None of these is a landing page, and each is reached by a deliberate nav
// click, so a chunk fetch is not on any critical first-paint path.  Every
// one of them is ALSO added to the idle-prefetch list in Layout.js so
// offline navigation keeps working (attorneys work on planes) — if you add
// another page here, add it there too or you trade bytes for a broken
// offline route.
const Invoices = lazy(() => import('./pages/Invoices'))
const Leads = lazy(() => import('./pages/Leads'))
const IntakeForms = lazy(() => import('./pages/IntakeForms'))
const IntakeFormEmbed = lazy(() => import('./pages/IntakeFormEmbed'))
const EngagementTemplates = lazy(() => import('./pages/EngagementTemplates'))
const EmailTemplates = lazy(() => import('./pages/EmailTemplates'))
const EmailSequences = lazy(() => import('./pages/EmailSequences'))
const MatterTemplates = lazy(() => import('./pages/MatterTemplates'))
const TimeReviewQueue = lazy(() => import('./pages/TimeReviewQueue'))
const Webhooks = lazy(() => import('./pages/Webhooks'))
const ReferralSources = lazy(() => import('./pages/ReferralSources'))
const ConsultBookings = lazy(() => import('./pages/ConsultBookings'))
const LeadAssignmentRules = lazy(() => import('./pages/LeadAssignmentRules'))
const Team = lazy(() => import('./pages/Team'))
const Settings = lazy(() => import('./pages/Settings'))
const Tags = lazy(() => import('./pages/Tags'))
const Payments = lazy(() => import('./pages/Payments'))
const Downloads = lazy(() => import('./pages/Downloads'))

// Portal pages — lazy-loaded so staff bundle isn't bloated
const PortalLogin = lazy(() => import('./pages/portal/PortalLogin'))
const PortalForgotPassword = lazy(() => import('./pages/portal/PortalForgotPassword'))
const PortalAcceptInvite = lazy(() => import('./pages/portal/PortalAcceptInvite'))
const PortalResetPassword = lazy(() => import('./pages/portal/PortalResetPassword'))
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'))
const PortalInvoices = lazy(() => import('./pages/portal/PortalInvoices'))
const PortalInvoiceDetail = lazy(() => import('./pages/portal/PortalInvoiceDetail'))
const PortalTrust = lazy(() => import('./pages/portal/PortalTrust'))
const PortalMatters = lazy(() => import('./pages/portal/PortalMatters'))
const PortalQuestionnaire = lazy(() => import('./pages/portal/PortalQuestionnaire'))
const PortalDocuments = lazy(() => import('./pages/portal/PortalDocuments'))
const PortalEvents = lazy(() => import('./pages/portal/PortalEvents'))
const PortalProfile = lazy(() => import('./pages/portal/PortalProfile'))

// Initialize Sentry at module load, before any React tree mounts.  No-op
// unless REACT_APP_SENTRY_DSN is set at build time.
initSentry()

// T2-B.next.B — one-time boot sweep of expired draft-recovery entries in
// localStorage.  Cheap-ish (iterates only `lextrack_draft:*` keys), but
// still an O(n) JSON.parse pass over all localStorage keys — deferred
// via setTimeout(0) so it doesn't block first paint (post-code panel
// finding by Kimi + Opus 4.8).  Removes >24h-old + malformed drafts so
// localStorage doesn't accumulate over months of use.
setTimeout(() => {
  try { sweepExpiredDrafts() } catch (e) { /* best-effort */ }
}, 0)

function AdminRoute({ children }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'admin' && !user.permissions?.is_admin) return <Navigate to="/dashboard" replace />
  return children
}

// Feedback #830 — /login for someone who is ALREADY signed in sends them into
// the app instead of showing the sign-in form again.  Decided ONCE, at mount:
// while the form is submitting, AuthContext sets `user` before Login.js runs
// its own navigate(), and re-checking on every render would race it (bouncing
// a must_change_password user to /dashboard instead of /profile, or dropping
// the "signed in with a backup code" notice).  AuthProvider renders nothing
// until its /me check finishes, so `user` here is already settled.
function GuestOnlyRoute({ children }) {
  const { user } = useAuth()
  const [signedInAtMount] = useState(() => !!user)
  if (signedInAtMount) {
    return <Navigate to={user?.must_change_password ? '/profile' : '/dashboard'} replace />
  }
  return children
}

function ProtectedRoute({ children }) {
  const { user } = useAuth()
  if (!user) return <Navigate to="/login" replace />
  return children
}

function PermissionRoute({ permission, feature, children }) {
  const { user } = useAuth()
  const { isEnabled, loaded } = useFeatureFlags()
  if (!user) return <Navigate to="/login" replace />
  // Feature-flag gate BEFORE permission gate: even admins should be bounced
  // out of a firm-disabled feature route so a hostile bookmark doesn't render
  // partial-broken UI.  Wait for flags to load before deciding (prevents
  // flash-redirect while the /api/settings call is in flight).
  if (feature && loaded && !isEnabled(feature)) return <Navigate to="/dashboard" replace />
  if (user.role === 'admin' || user.permissions?.is_admin) return children
  // Ship 7b F2.2 — accept `permission` as either a string OR array of strings
  // (any-one-of / OR semantic).  Existing single-string callers keep working;
  // new callers can pass `["can_manage_leads", "can_edit_contacts"]` to widen.
  const perms = Array.isArray(permission) ? permission : [permission]
  const has = perms.some((p) => user.permissions?.[p])
  if (!has) return <Navigate to="/dashboard" replace />
  return children
}

function AppRoutes() {
  // Automatic zoom: pop-outs (/popup/*) are never scaled (lib/displayZoom.js).
  useDisplayZoomRouteSync()
  return (
    // Outer Suspense + ChunkLoadErrorBoundary — catches lazy LAYOUTS
    // (PopupLayout) and any lazy component not wrapped by a nearer
    // boundary.  Each Layout (Layout / PortalLayout / PopupLayout) now
    // renders its OWN inner ChunkLoadErrorBoundary + Suspense around
    // <Outlet /> so lazy PAGE navigations don't unmount the shell
    // (sidebar / timer / nav state).  ChunkLoadError bounces to a
    // one-click "Update available — reload" UI instead of white-screen.
    <ChunkLoadErrorBoundary>
    <Suspense fallback={<PageLoading />}>
    <Routes>
      {/* Portal routes — fully separate auth domain.

          These deliberately carry NO `feature="portal"` prop, and must not be
          wrapped in PermissionRoute.  That is not an oversight — both of the
          mechanisms the staff routes below use are structurally wrong here:

            • PermissionRoute calls useAuth() and, finding no STAFF user,
              redirects to /login — the staff login page.  Applied here it
              would bounce every real client out of their own portal.
            • useFeatureFlags() reads the feature flags from GET /api/settings
              using the staff `api` client.  That endpoint is behind
              verifyStaff, and the portal uses a separate `portalApi` client
              with a separate JWT, so for a client the call 401s, the hook
              fails soft to "everything enabled", and the gate would never
              fire even if the flag were off.

          Enforcement of the `portal` feature flag therefore lives server-side,
          in the plugin-wide onRequest kill switch in api/src/routes/portal.js,
          which also covers firm_settings.portal_enabled.  Every portal page
          already renders the API's error text, so a switched-off portal shows
          the server's "your firm has turned off the client portal" message.
          Adding a client-side gate here would need a flag source the portal
          can actually read (e.g. GET /api/portal/firm, which is public) — not
          the staff-only one used above. */}
      <Route path="/portal/login" element={<PortalAuthProvider><PortalLogin /></PortalAuthProvider>} />
      <Route path="/portal/forgot-password" element={<PortalForgotPassword />} />
      <Route path="/portal/accept-invite" element={<PortalAcceptInvite />} />
      <Route path="/portal/reset-password" element={<PortalResetPassword />} />
      <Route path="/portal" element={<PortalAuthProvider><PortalLayout /></PortalAuthProvider>}>
        <Route index element={<Navigate to="/portal/dashboard" replace />} />
        <Route path="dashboard" element={<PortalDashboard />} />
        <Route path="invoices" element={<PortalInvoices />} />
        <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
        <Route path="trust" element={<PortalTrust />} />
        <Route path="matters" element={<PortalMatters />} />
        <Route path="matters/:id/questionnaire" element={<PortalQuestionnaire />} />
        <Route path="documents" element={<PortalDocuments />} />
        <Route path="events" element={<PortalEvents />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>

      <Route path="/login" element={<GuestOnlyRoute><Login /></GuestOnlyRoute>} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/emergency-lock" element={<EmergencyLock />} />

      {/* Public intake form — no auth, no Layout wrapper.  Directly
          accessible from the internet at /intake/:firmSlug/:formSlug.
          Backend is at /api/public/intake/:firm_slug/:form_slug. */}
      <Route path="/intake/:firmSlug/:formSlug" element={<PublicIntake />} />
      <Route path="/sign/:publicId" element={<EngagementSign />} />
      {/* Public intake questionnaire — no auth, no Layout.  Token lives in the
          URL FRAGMENT (#t=…) and is scrubbed on mount; see QuestionnaireFill. */}
      <Route path="/questionnaire/:publicId" element={<QuestionnaireFill />} />
      {/* #816 — prospective-client record uploads.  Same fragment-token
          posture as the questionnaire: #t=… is read on mount, stashed in
          sessionStorage, then replaceState-scrubbed.  After /verify the
          path-scoped HttpOnly session cookie is the credential, and the
          capability is WRITE-ONLY — there is no listing endpoint, so a
          forwarded link cannot read back someone else's records. */}
      <Route path="/records/:publicId" element={<RecordUpload />} />
      {/* Public consult cancellation — no auth, no Layout.  The cancellation
          token lives in the URL FRAGMENT (#t=…), never the path: a path is
          written verbatim into Apache's access log and leaks via Referer.
          The bare path is the live route; the :token form is kept only so a
          link mailed before the fragment move still works — the component
          moves that token into sessionStorage and scrubs the path. */}
      <Route path="/consult/cancel" element={<ConsultCancel />} />
      <Route path="/consult/cancel/:token" element={<ConsultCancel />} />

      {/* Public invoice payment — no auth, no Layout.  Signed pay token
          lives in the URL FRAGMENT (#t=…) — never in the path (Wave 6
          finding #6: fragments do not appear in Apache logs, browser
          history, or Referer headers).  PublicPay reads it from
          window.location.hash on mount and immediately history.replaceState-
          scrubs it out of the visible URL. */}
      <Route path="/pay" element={<PublicPay />} />
      <Route path="/pay/success" element={<PublicPay />} />
      {/* Legacy path routes preserved so pre-fix emailed links keep working
          during rollout (the loadInvoiceByToken plaintext fallback matches
          rows minted before the hash-column deploy). */}
      <Route path="/pay/:token" element={<PublicPay />} />
      <Route path="/pay/:token/success" element={<PublicPay />} />

      {/* Pop-out windows (Electron-only) — each /popup/* route renders in
          its own OS BrowserWindow with PopupLayout (no sidebar, no nav).
          ProtectedRoute still applies via the AuthProvider higher up —
          the new window shares localStorage/sessionStorage (auth) with
          the main window via the 'persist:lextrack' partition. */}
      {/* Desktop 1.0.13 timer bar (James, 2026-10-01) — the floating mini timer
          window.  Deliberately OUTSIDE PopupLayout (no report-issue chip, no
          title strip) and NOT behind ProtectedRoute: a redirect to the sign-in
          form would render a login page inside a 48px window.  TimerBar itself
          shows nothing but a "Sign in to Omnia" link while no one is signed in
          — no matter names, no times — and only reads localStorage. */}
      <Route path="/popup/timer-bar" element={<TimerBar />} />
      <Route path="/popup" element={<ProtectedRoute><PopupLayout /></ProtectedRoute>}>
        <Route path="matter/:id" element={<PopupMatter />} />
        <Route path="timer/new" element={<PopupTimer mode="new" />} />
        <Route path="timer/resume/:entry_id" element={<PopupTimer mode="resume" />} />

        {/* Full-page pop-outs (Surface A/B/C/D — sidebar right-click, row
            right-click, header ⤴, Ctrl+click).  Same page components as
            the main app, but rendered inside PopupLayout so the popup
            window has NO sidebar / NO fab — focused view of one page.
            Permission gates mirror the main routes so a user can only
            pop out pages they're allowed to see. */}
        <Route path="page/dashboard"        element={<PermissionRoute permission="dashboard"><Dashboard /></PermissionRoute>} />
        <Route path="page/dailies"          element={<PermissionRoute permission="dailies"><Dailies /></PermissionRoute>} />
        <Route path="page/clients"          element={<PermissionRoute permission="clients"><Clients /></PermissionRoute>} />
        <Route path="page/clients/:id"      element={<PermissionRoute permission="clients"><ClientDetail /></PermissionRoute>} />
        <Route path="page/leads"            element={<PermissionRoute permission="leads"><Leads /></PermissionRoute>} />
        <Route path="page/leads/:id"        element={<PermissionRoute permission="leads"><Leads /></PermissionRoute>} />
        <Route path="page/intake-forms"     element={<PermissionRoute permission="can_manage_intake_forms"><IntakeForms /></PermissionRoute>} />
        <Route path="page/intake-forms/:id" element={<PermissionRoute permission="can_manage_intake_forms"><IntakeForms /></PermissionRoute>} />
        <Route path="page/intake-forms/:id/embed" element={<PermissionRoute permission="can_manage_intake_forms"><IntakeFormEmbed /></PermissionRoute>} />
        <Route path="page/engagement-templates" element={<PermissionRoute permission="can_manage_engagement_letters"><EngagementTemplates /></PermissionRoute>} />
        <Route path="page/engagement-templates/:id" element={<PermissionRoute permission="can_manage_engagement_letters"><EngagementTemplates /></PermissionRoute>} />
        <Route path="page/email-templates" element={<PermissionRoute permission="can_manage_email_templates"><EmailTemplates /></PermissionRoute>} />
        <Route path="page/email-templates/:id" element={<PermissionRoute permission="can_manage_email_templates"><EmailTemplates /></PermissionRoute>} />
        <Route path="page/email-sequences" element={<PermissionRoute permission="can_manage_email_sequences"><EmailSequences /></PermissionRoute>} />
        <Route path="page/email-sequences/:id" element={<PermissionRoute permission="can_manage_email_sequences"><EmailSequences /></PermissionRoute>} />
        <Route path="page/matter-templates" element={<PermissionRoute permission="matters"><MatterTemplates /></PermissionRoute>} />
        <Route path="page/custom-fields"    element={<AdminRoute><CustomFields /></AdminRoute>} />
        <Route path="page/roles"            element={<AdminRoute><Roles /></AdminRoute>} />
        <Route path="page/time-review-queue" element={<PermissionRoute permission="time_entries"><TimeReviewQueue /></PermissionRoute>} />
        <Route path="page/webhooks"             element={<AdminRoute><Webhooks /></AdminRoute>} />
        <Route path="page/referral-sources"     element={<PermissionRoute permission="leads"><ReferralSources /></PermissionRoute>} />
        <Route path="page/tags"                 element={<PermissionRoute permission={["can_manage_leads", "can_edit_contacts"]}><Tags /></PermissionRoute>} />
        <Route path="page/consult-bookings"     element={<PermissionRoute permission="leads"><ConsultBookings /></PermissionRoute>} />
        <Route path="page/lead-assignment-rules" element={<PermissionRoute permission="can_manage_leads"><LeadAssignmentRules /></PermissionRoute>} />
        <Route path="page/matters"          element={<PermissionRoute permission="matters"><Matters /></PermissionRoute>} />
        <Route path="page/matters/:id"      element={<PermissionRoute permission="matters"><MatterDetail /></PermissionRoute>} />
        <Route path="page/time-entries"     element={<PermissionRoute permission="time_entries"><TimeEntries /></PermissionRoute>} />
        <Route path="page/billing"          element={<PermissionRoute permission="billing_tab"><Billing /></PermissionRoute>} />
        <Route path="page/invoices"         element={<PermissionRoute permission="invoices"><Invoices /></PermissionRoute>} />
        <Route path="page/invoices/:id"     element={<PermissionRoute permission="invoices"><InvoiceDetail /></PermissionRoute>} />
        <Route path="page/receipts"         element={<PermissionRoute permission="receipts"><Receipts /></PermissionRoute>} />
        <Route path="page/payments"         element={<PermissionRoute permission="payments"><Payments /></PermissionRoute>} />
        <Route path="page/contacts"         element={<PermissionRoute permission="contacts"><Contacts /></PermissionRoute>} />
        <Route path="page/sol"              element={<PermissionRoute permission="sol_tracker" feature="sol_tracker"><SOLTracker /></PermissionRoute>} />
        <Route path="page/conflicts"        element={<PermissionRoute permission="conflicts" feature="conflicts"><ConflictChecker /></PermissionRoute>} />
        <Route path="page/court-rules"      element={<PermissionRoute permission="matters" feature="court_rules"><CourtRules /></PermissionRoute>} />
        <Route path="page/communications"   element={<PermissionRoute permission="communications" feature="communications"><Communications /></PermissionRoute>} />
        <Route path="page/calendar"         element={<PermissionRoute permission="calendar"><Calendar /></PermissionRoute>} />
        <Route path="page/deadlines"        element={<PermissionRoute permission="calendar"><Deadlines /></PermissionRoute>} />
        <Route path="page/discovery"        element={<PermissionRoute permission="matters" feature="discovery"><Discovery /></PermissionRoute>} />
        <Route path="page/trust"            element={<PermissionRoute permission="trust" feature="trust"><Trust /></PermissionRoute>} />
        <Route path="page/tasks"            element={<PermissionRoute permission="tasks"><Tasks /></PermissionRoute>} />
        <Route path="page/accounting"       element={<PermissionRoute permission="can_view_accounting"><Accounting /></PermissionRoute>} />
        <Route path="page/reports"          element={<PermissionRoute permission={['reports', 'time_entries', 'can_post_time']}><Reports /></PermissionRoute>} />
        <Route path="page/attorney-productivity" element={<PermissionRoute permission="time_entries" feature="attorney_productivity"><AttorneyProductivity /></PermissionRoute>} />
        <Route path="page/custom-reports" element={<PermissionRoute permission="reports" feature="custom_reports"><CustomReports /></PermissionRoute>} />
        <Route path="page/batch-invoicing"  element={<PermissionRoute permission="batch_invoicing" feature="batch_invoicing"><BatchInvoicing /></PermissionRoute>} />
        <Route path="page/downloads"        element={<PermissionRoute permission="downloads"><Downloads /></PermissionRoute>} />
        <Route path="page/settings"         element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="page/profile"          element={<Profile />} />
        <Route path="page/help"             element={<Help />} />
        <Route path="page/team"             element={<PermissionRoute permission="team"><Team /></PermissionRoute>} />
        <Route path="page/admin"            element={<AdminRoute><AdminHome /></AdminRoute>} />
        <Route path="page/audit-log"        element={<AdminRoute><AuditLog /></AdminRoute>} />
        <Route path="page/activity-feed"    element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
        <Route path="page/feedback"         element={<AdminRoute><Feedback /></AdminRoute>} />
        <Route path="page/import"           element={<AdminRoute><Import /></AdminRoute>} />
        <Route path="page/time-entry-duplicates" element={<AdminRoute><TimeEntryDuplicates /></AdminRoute>} />
        <Route path="page/clients/merge"    element={<PermissionRoute permission="can_delete_clients"><ClientMerge /></PermissionRoute>} />
        <Route path="page/contacts/duplicates" element={<PermissionRoute permission="can_delete_contacts"><ContactDuplicates /></PermissionRoute>} />
        <Route path="page/matters/duplicates"  element={<AdminRoute><MatterDuplicates /></AdminRoute>} />
      </Route>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<PermissionRoute permission="dashboard"><Dashboard /></PermissionRoute>} />
        <Route path="dailies" element={<PermissionRoute permission="dailies"><Dailies /></PermissionRoute>} />
        <Route path="clients" element={<PermissionRoute permission="clients"><Clients /></PermissionRoute>} />
        <Route path="clients/:id" element={<PermissionRoute permission="clients"><ClientDetail /></PermissionRoute>} />
        <Route path="leads" element={<PermissionRoute permission="leads"><Leads /></PermissionRoute>} />
        <Route path="leads/:id" element={<PermissionRoute permission="leads"><Leads /></PermissionRoute>} />
        <Route path="intake-forms" element={<PermissionRoute permission="can_manage_intake_forms"><IntakeForms /></PermissionRoute>} />
        <Route path="intake-forms/:id" element={<PermissionRoute permission="can_manage_intake_forms"><IntakeForms /></PermissionRoute>} />
        <Route path="intake-forms/:id/embed" element={<PermissionRoute permission="can_manage_intake_forms"><IntakeFormEmbed /></PermissionRoute>} />
        <Route path="engagement-templates" element={<PermissionRoute permission="can_manage_engagement_letters"><EngagementTemplates /></PermissionRoute>} />
        <Route path="engagement-templates/:id" element={<PermissionRoute permission="can_manage_engagement_letters"><EngagementTemplates /></PermissionRoute>} />
        <Route path="email-templates" element={<PermissionRoute permission="can_manage_email_templates"><EmailTemplates /></PermissionRoute>} />
        <Route path="email-templates/:id" element={<PermissionRoute permission="can_manage_email_templates"><EmailTemplates /></PermissionRoute>} />
        <Route path="email-sequences" element={<PermissionRoute permission="can_manage_email_sequences"><EmailSequences /></PermissionRoute>} />
        <Route path="email-sequences/:id" element={<PermissionRoute permission="can_manage_email_sequences"><EmailSequences /></PermissionRoute>} />
        <Route path="matter-templates" element={<PermissionRoute permission="matters"><MatterTemplates /></PermissionRoute>} />
        <Route path="custom-fields" element={<AdminRoute><CustomFields /></AdminRoute>} />
        <Route path="roles" element={<AdminRoute><Roles /></AdminRoute>} />
        <Route path="time-review-queue" element={<PermissionRoute permission="time_entries"><TimeReviewQueue /></PermissionRoute>} />
        <Route path="webhooks" element={<AdminRoute><Webhooks /></AdminRoute>} />
        <Route path="referral-sources" element={<PermissionRoute permission="leads"><ReferralSources /></PermissionRoute>} />
        <Route path="tags" element={<PermissionRoute permission={["can_manage_leads", "can_edit_contacts"]}><Tags /></PermissionRoute>} />
        <Route path="consult-bookings" element={<PermissionRoute permission="leads"><ConsultBookings /></PermissionRoute>} />
        <Route path="lead-assignment-rules" element={<PermissionRoute permission="can_manage_leads"><LeadAssignmentRules /></PermissionRoute>} />
        <Route path="matters" element={<PermissionRoute permission="matters"><Matters /></PermissionRoute>} />
        <Route path="matters/:id" element={<PermissionRoute permission="matters"><MatterDetail /></PermissionRoute>} />
        <Route path="time-entries" element={<PermissionRoute permission="time_entries"><TimeEntries /></PermissionRoute>} />
        <Route path="billing" element={<PermissionRoute permission="billing_tab"><Billing /></PermissionRoute>} />
        <Route path="invoices" element={<PermissionRoute permission="invoices"><Invoices /></PermissionRoute>} />
        <Route path="batch-invoicing" element={<PermissionRoute permission="batch_invoicing" feature="batch_invoicing"><BatchInvoicing /></PermissionRoute>} />
        <Route path="receipts" element={<PermissionRoute permission="receipts"><Receipts /></PermissionRoute>} />
        <Route path="payments" element={<PermissionRoute permission="payments"><Payments /></PermissionRoute>} />
        <Route path="contacts" element={<PermissionRoute permission="contacts"><Contacts /></PermissionRoute>} />
        <Route path="sol" element={<PermissionRoute permission="sol_tracker" feature="sol_tracker"><SOLTracker /></PermissionRoute>} />
        <Route path="conflicts" element={<PermissionRoute permission="conflicts" feature="conflicts"><ConflictChecker /></PermissionRoute>} />
        <Route path="court-rules" element={<PermissionRoute permission="matters" feature="court_rules"><CourtRules /></PermissionRoute>} />
        <Route path="communications" element={<PermissionRoute permission="communications" feature="communications"><Communications /></PermissionRoute>} />
        <Route path="calendar" element={<PermissionRoute permission="calendar"><Calendar /></PermissionRoute>} />
        <Route path="deadlines" element={<PermissionRoute permission="calendar"><Deadlines /></PermissionRoute>} />
        <Route path="discovery" element={<PermissionRoute permission="matters" feature="discovery"><Discovery /></PermissionRoute>} />
        <Route path="trust" element={<PermissionRoute permission="trust" feature="trust"><Trust /></PermissionRoute>} />
        <Route path="tasks" element={<PermissionRoute permission="tasks"><Tasks /></PermissionRoute>} />
        <Route path="team" element={<PermissionRoute permission="team"><Team /></PermissionRoute>} />
        <Route path="accounting" element={<PermissionRoute permission="can_view_accounting"><Accounting /></PermissionRoute>} />
        {/* Reports (and its pop-out twin above): Reports, OR Time Entries, OR
            Post time — any one (Hestia via James, 2026-11-02).  Without
            Reports the page offers the Timesheet alone (pages/Reports.js
            hasReportPerm); lib/navItems.js gates the sidebar entry alike. */}
        <Route path="reports" element={<PermissionRoute permission={['reports', 'time_entries', 'can_post_time']}><Reports /></PermissionRoute>} />
        <Route path="attorney-productivity" element={<PermissionRoute permission="time_entries" feature="attorney_productivity"><AttorneyProductivity /></PermissionRoute>} />
        <Route path="custom-reports" element={<PermissionRoute permission="reports" feature="custom_reports"><CustomReports /></PermissionRoute>} />
        <Route path="admin" element={<AdminRoute><AdminHome /></AdminRoute>} />
        <Route path="import" element={<AdminRoute><Import /></AdminRoute>} />
        <Route path="audit-log" element={<AdminRoute><AuditLog /></AdminRoute>} />
        <Route path="activity-feed" element={<ProtectedRoute><ActivityFeed /></ProtectedRoute>} />
        <Route path="clients/merge" element={<PermissionRoute permission="can_delete_clients"><ClientMerge /></PermissionRoute>} />
        <Route path="contacts/duplicates" element={<PermissionRoute permission="can_delete_contacts"><ContactDuplicates /></PermissionRoute>} />
        <Route path="matters/duplicates"  element={<AdminRoute><MatterDuplicates /></AdminRoute>} />
        <Route path="feedback" element={<AdminRoute><Feedback /></AdminRoute>} />
        <Route path="time-entry-duplicates" element={<AdminRoute><TimeEntryDuplicates /></AdminRoute>} />
        <Route path="downloads" element={<PermissionRoute permission="downloads"><Downloads /></PermissionRoute>} />
        <Route path="profile" element={<Profile />} />
        <Route path="help" element={<Help />} />
        <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="invoices/:id" element={<PermissionRoute permission="invoices"><InvoiceDetail /></PermissionRoute>} />
        <Route path="search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
      </Route>
    </Routes>
    </Suspense>
    </ChunkLoadErrorBoundary>
  )
}

// Fallback UI shown when Sentry catches an unrecoverable React render
// error.  Reload is the safest recovery — some state may be corrupt.
function CrashFallback({ error, resetError }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="text-5xl mb-4">💥</div>
        <h1 className="text-xl font-semibold text-gray-800 mb-2">Something went wrong</h1>
        <p className="text-sm text-gray-500 mb-4">
          The app hit an unexpected error.  Reload to recover.  If the problem persists, use Report Issue in the sidebar.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium">
          Reload
        </button>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <SentryErrorBoundary fallback={CrashFallback}>
    <ThemeProvider>
      <ToastProvider>
        <PromptProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthProvider>
        </PromptProvider>
      </ToastProvider>
    </ThemeProvider>
    </SentryErrorBoundary>
  )
}